// --- Helpers para payload de facturación ---
export const isRUC = (id = "") => typeof id === "string" && id.trim().length === 13;

// Divide "NOMBRES APELLIDOS" en {nombres, apellidos}
export const splitNombre = (nombreCompleto = "") => {
  const parts = String(nombreCompleto).trim().split(/\s+/);
  if (parts.length <= 1) return { nombres: nombreCompleto, apellidos: "" };
  const mitad = Math.floor(parts.length / 2);
  return {
    nombres: parts.slice(0, mitad).join(" "),
    apellidos: parts.slice(mitad).join(" "),
  };
};

// Construye datosContratante desde el carrito
// Acepta opcionalmente los objetos ya resueltos {value,label} de provincia y ciudad
export const makeDatosContratante = (carrito = {}, provinciaResuelta = null, ciudadResuelta = null) => {
  const persona = carrito && carrito.datosContratantePersona ? carrito.datosContratantePersona : null;
  const empresa = carrito && carrito.datosContratanteEmpresa ? carrito.datosContratanteEmpresa : null;
  const ubicacion = carrito && carrito.datosUbicacion ? carrito.datosUbicacion : null;

  // Si hay empresa (RUC), retornar todos los campos disponibles
  if (empresa && empresa.cliIdentificacion) {
    // Fallbacks en caso de que no vengan resueltos ni en el objeto empresa
    const fallbackProv = (ubicacion && ubicacion.provincia && ubicacion.provincia.value) || (persona && persona.cliProvincia) || null;
    const fallbackCiu  = (ubicacion && ubicacion.ciudad    && ubicacion.ciudad.value)    || (persona && persona.cliCiudad)    || null;

    // provincia y ciudad como objetos {value, label} - se reciben resueltos como param opcional
    const provinciaObj = provinciaResuelta || { value: empresa.cliProvincia || fallbackProv, label: "" };
    const ciudadObj    = ciudadResuelta    || { value: empresa.cliCiudad    || fallbackCiu, label: "" };

    return {
      tipoIdentificacion: empresa.tipoIdentificacion || "2",
      identificacion: String(empresa.cliIdentificacion || ""),
      razonSocial: String(empresa.cliRazonSocial || ""),
      correo: empresa.cliEmail || null,
      telefono: empresa.cliTelefono || empresa.cliCelular || null,
      celular: empresa.cliCelular || empresa.cliTelefono || null,
      direccion: empresa.cliDireccion || null,
      provincia: provinciaObj,
      ciudad: ciudadObj,
      fechaConstitucion: empresa.cliFechaConstitucion || null,
    };
  }

  // Caso persona
  const id = String((persona && persona.cliIdentificacion) || "");
  const nombreCompleto = (persona && persona.nombreCompleto) ? persona.nombreCompleto : "";
  const split = splitNombre(nombreCompleto);
  const nombresSplit = split.nombres;
  const apellidosSplit = split.apellidos;

  // Para persona también incluimos provincia y ciudad si están disponibles
  const provVal = (ubicacion && ubicacion.provincia && ubicacion.provincia.value) || (persona && persona.cliProvincia) || null;
  const ciuVal  = (ubicacion && ubicacion.ciudad    && ubicacion.ciudad.value)    || (persona && persona.cliCiudad)    || null;

  return {
    // fallback: si detecta 13 dígitos, marca RUC
    tipoIdentificacion: isRUC(id) ? "2" : "1",
    identificacion: id,
    nombres: nombresSplit || [persona && persona.cliNombre1, persona && persona.cliNombre2].filter(Boolean).join(" "),
    apellidos: apellidosSplit || [persona && persona.cliApellido1, persona && persona.cliApellido2].filter(Boolean).join(" "),
    estadoCivil: (persona && typeof persona.cliEstadoCivil !== "undefined") ? persona.cliEstadoCivil : null,
    genero: (persona && typeof persona.cliGenero !== "undefined") ? persona.cliGenero : null,
    telefono: (persona && typeof persona.cliTelefono !== "undefined") ? persona.cliTelefono : null,
    direccion: (persona && typeof persona.cliDireccion !== "undefined") ? persona.cliDireccion : null,
    fechaNacimiento: (persona && typeof persona.cliFechaNacimiento !== "undefined") ? persona.cliFechaNacimiento : null,
    provincia: provinciaResuelta || { value: provVal, label: "" },
    ciudad: ciudadResuelta || { value: ciuVal, label: "" },
  };
};

// Valores por defecto de provincia y ciudad para el pagador
const PROVINCIA_PAGADOR_DEFAULT = { value: "19", label: "PICHINCHA" };
const CIUDAD_PAGADOR_DEFAULT    = { value: "122", label: "QUITO" };

// Construye datosPagadorFacturacion con el 1er pagador del producto
export const makeDatosPagadorFacturacion = (producto = {}) => {
  const arrPagadores = (producto && Array.isArray(producto.pagadores)) ? producto.pagadores : [];
  const p = arrPagadores.length > 0 ? arrPagadores[0] : null;
  if (!p) return null;

  const identificacion = String(p.cliIdentificacion || "");
  if (isRUC(identificacion)) {
    // Pagador empresa
    return {
      tipoIdentificacion: "2",
      identificacion: identificacion,
      razonSocial: String(p.razonSocial || p.nombreCompleto || ""),
      telefono: (typeof p.celular !== "undefined") ? p.celular : null,
      email: (typeof p.email !== "undefined") ? p.email : null,
      direccion: (typeof p.direccion !== "undefined") ? p.direccion : null,
      provincia: PROVINCIA_PAGADOR_DEFAULT,
      ciudad: CIUDAD_PAGADOR_DEFAULT,
    };
  }

  // Pagador persona
  const nombreCompleto = p.nombreCompleto ? String(p.nombreCompleto) : "";
  const split = splitNombre(nombreCompleto);
  return {
    tipoIdentificacion: "1",
    identificacion: identificacion,
    nombres: split.nombres,
    apellidos: split.apellidos,
    telefono: (typeof p.celular !== "undefined") ? p.celular : null,
    email: (typeof p.email !== "undefined") ? p.email : null,
    direccion: (typeof p.direccion !== "undefined") ? p.direccion : null,
    provincia: PROVINCIA_PAGADOR_DEFAULT,
    ciudad: CIUDAD_PAGADOR_DEFAULT,
  };
};

export const consultarDatosIdentificacion = async (identificacion, tipo) => {
  try {
    const { apiPost2 } = await import("../../../api");
    const { urlConsultarIdentificacion } = await import("../../../constants/urls");

    const response = await apiPost2(urlConsultarIdentificacion, {
      empresa: "AIGMETROPOLITANA",
      identificador: identificacion,
      tipo: tipo === "1" || tipo === "cedula" ? "cedula" : tipo === "2" || tipo === "ruc" ? "ruc" : "pasaporte"
    });

    if (response && response.transaccion && response.datosIdentificador && response.datosIdentificador.length > 0) {
      return response.datosIdentificador[0];
    }
    return null;
  } catch (error) {
    console.error("Error consultando identificación:", error);
    return null;
  }
};

/**
 * Consulta las APIs de provincias y ciudades para resolver los labels
 * a partir de los códigos numéricos almacenados en datosContratanteEmpresa.
 * Retorna { provinciaObj, ciudadObj } con forma { value, label }.
 * En caso de error, retorna los objetos con label vacío pero value correcto.
 */
export const resolverUbicacionEmpresa = async (carrito = {}) => {
  const empresa = carrito && carrito.datosContratanteEmpresa ? carrito.datosContratanteEmpresa : {};
  const ubicacion = carrito && carrito.datosUbicacion ? carrito.datosUbicacion : {};
  const persona = carrito && carrito.datosContratantePersona ? carrito.datosContratantePersona : {};

  // Búsqueda en cascada de los códigos
  const codProvincia = empresa.cliProvincia || (ubicacion.provincia && ubicacion.provincia.value) || persona.cliProvincia || null;
  const codCiudad    = empresa.cliCiudad    || (ubicacion.ciudad    && ubicacion.ciudad.value)    || persona.cliCiudad    || null;

  let provinciaObj = { value: codProvincia, label: "" };
  let ciudadObj    = { value: codCiudad,    label: "" };

  try {
    const { apiGet, apiPost2 } = await import("../../../api");
    const { urlListarProvincias, urlCiudadesAP } = await import("../../../constants/urls");

    // 1) Resolver label de provincia
    if (codProvincia) {
      const resProv = await apiGet(urlListarProvincias);
      if (resProv && resProv.transaccion && Array.isArray(resProv.provinciaFormato)) {
        const encontrada = resProv.provinciaFormato.find(
          (p) => p.provCodigoCore === codProvincia || String(p.provCodigoCore) === String(codProvincia)
        );
        if (encontrada) {
          provinciaObj = { value: codProvincia, label: encontrada.label || encontrada.provNombre || "" };
        }
      }

      // 2) Resolver label de ciudad (requiere el código de provincia para filtrar)
      if (codCiudad) {
        const resCiu = await apiPost2(urlCiudadesAP, {
          empresa: "AIGMETROPOLITANA",
          codProvincia: codProvincia,
          ramo: "travel"
        });
        if (resCiu && resCiu.transaccion && Array.isArray(resCiu.ciudades)) {
          const encontrada = resCiu.ciudades.find(
            (c) => c.value === codCiudad || String(c.value) === String(codCiudad)
          );
          if (encontrada) {
            ciudadObj = { value: codCiudad, label: encontrada.label || "" };
          }
        }
      }
    }
  } catch (error) {
    console.error("Error resolviendo ubicación del contratante:", error);
    // Fallback: retorna objetos con label vacío pero value correcto
  }

  return { provinciaObj, ciudadObj };
};
